import { DatePicker, Select, Input, Form, Row, Col, Space, Typography, Tooltip, theme } from "antd";
import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { isAuthenticated } from "../Auth/auth";
import DragUpload from "../DragUpload/DragUpload";
import { Error, Success } from "../Messages/Messages";
import "./Projects.css";
import MainButtonComp from "../Buttons/MainButtonComp";
import TextArea from "antd/es/input/TextArea";
import GeneralModal from "../../serviceProvider/components/General.modal";
import { SocketContext } from "../../context/SocketContext";
import { currenciesArray } from "../../utils/currenciesArray";
import { generalErrorAlert } from "../GeneralErrorAlert";
import InventoryUsage from "../Inventory/InventoryUsage";
import { projectStatusArray } from "../../utils/projectStatusArray";
import dayjs from "dayjs";
import { InfoCircleOutlined } from "@ant-design/icons";

import { useTranslation } from "react-i18next";
import CustomerBranchPicker from "../Billing/CustomerBranchPicker";
import CustomerSelect from "../Billing/CustomerSelect";
import { findBranch, sameId } from "../Billing/customerBranches";
const { Title, Text } = Typography;

const AddEditProject = ({
  inventoryList,
  data,
  updateParent,
  serviceProviderId: propServiceProviderId,
  employeeObject
}) => {
  const { t } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const isEditMode = !!data?._id;

  let serviceProviderId =
    propServiceProviderId ||
    (isAuthenticated()?.employer?._id
      ? isAuthenticated()?.employer?._id
      : isAuthenticated()?.serviceProviderId);

  const socket = useContext(SocketContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [clientsArray, setClientsArray] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [form] = Form.useForm();
  // Niederlassung of the project's customer: the project's invoices and offers default to it
  const [branchSel, setBranchSel] = useState({ clientBranch: null, billTo: "branch" });
  const clientId = Form.useWatch("client", form);
  const selectedClient = clientsArray.find(o => sameId(o.value, clientId))?.customer || null;

  const getEmployees = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/employees/employee/creator/${serviceProviderId}`,
        {
          headers: { Authorization: "Bearer " + localStorage.getItem("token") }
        }
      );
      if (res.status === 200 || res.status === 304) {
        const makeOptions = res.data?.map(d => ({
          label: d?.firstName + " " + d?.lastName,
          value: d?._id
        }));
        setEmployees(makeOptions);
      }
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const getClients = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/users/clients/limited`,
        { pageSize: 100000000, pageNo: 0, serviceProvider: serviceProviderId },
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );
      setLoading(false);
      if (res.status === 200 || res.status === 304) {
        const makeOptions = res.data?.data?.map(d => ({
          label: d?.firstName + " " + d?.lastName,
          value: d?._id,
          customer: d
        }));
        setClientsArray(makeOptions);
      }
    } catch (err) {
      setLoading(false);
      generalErrorAlert(err);
    }
  };

  const getDepartments = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/departments/limited`,
        { serviceProvider: serviceProviderId, pageSize: 10000000, pageNo: 0 },
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );
      if (res.status === 200 || res.status === 304) {
        const makeOptions = res.data?.data?.map(d => ({ label: d?.title, value: d?._id }));
        setDepartments(makeOptions);
      }
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      getDepartments();
      getEmployees();
      getClients();

      if (isEditMode && data) {
        form.setFieldsValue({
          projectName: data?.projectName || "",
          client: data?.client?._id || data?.client || null,
          priority: data?.priority || "MEDIUM",
          startDate: data?.startDate ? dayjs(data?.startDate) : null,
          endDate: data?.endDate ? dayjs(data?.endDate) : null,
          projectLeader: data?.projectLeader?._id || data?.projectLeader || null,
          financialController: data?.financialController?._id || data?.financialController || null,
          team: data.team?.map(team => team?._id || team) || [],
          rateType: data?.rateType || "HOURLY",
          currency: data?.currency || "USD",
          amount: data?.amount || "",
          description: data?.description || "",
          department: data?.department?._id || data?.department || null,
          status: data?.status || "JUST ADDED",
          inventory:
            data?.inventory?.map(item => ({
              item: item?.item?._id || item?.item,
              total: item?.total,
              minimumAmount: item?.minimumAmount
            })) || []
        });
        setBranchSel({
          clientBranch: data?.clientBranch || null,
          billTo: data?.billTo || "branch"
        });
      } else {
        form.resetFields();
        setBranchSel({ clientBranch: null, billTo: "branch" });
        if (employeeObject?._id) {
          form.setFieldsValue({
            team: [employeeObject._id],
            department: employeeObject?.department?._id || employeeObject?.department || null
          });
        }
      }
    }
  }, [isModalOpen, data, isEditMode, employeeObject]);

  const showModal = () => setIsModalOpen(true);
  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const onFinish = async values => {
    setLoading(true);
    let finalTeam = values.team || [];
    if (employeeObject?._id && !finalTeam.includes(employeeObject._id)) {
      finalTeam = [...finalTeam, employeeObject._id];
    }
    const payload = {
      ...values,
      team: finalTeam,
      serviceProvider: serviceProviderId,
      clientBranch: branchSel.clientBranch || null,
      billTo: branchSel.billTo || "branch"
    };
    const url = isEditMode
      ? `${process.env.REACT_APP_BACKEND_URL}/projects/update/${data._id}`
      : `${process.env.REACT_APP_BACKEND_URL}/projects/add`;
    const method = isEditMode ? "put" : "post";

    try {
      const res = await axios[method](url, payload, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      });
      setLoading(false);
      if (res.status === 200 || res.status === 304) {
        Success(
          res.data.successMessage ||
            (isEditMode ? "Project updated successfully" : "Project added successfully")
        );
        handleCancel();
        if (updateParent) updateParent();
        socket.emit("refresh service provider data", {
          user: isAuthenticated()?.serviceProviderId
        });
      } else {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      setLoading(false);
      generalErrorAlert(err);
    }
  };

  const items = [
    {
      key: "1",
      label: t("Project Details"),
      children: (
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="premium-form"
          initialValues={{
            priority: "MEDIUM",
            status: "JUST ADDED",
            currency: "USD",
            rateType: "HOURLY"
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="projectName"
                label={t("Project Name")}
                rules={[{ required: true, message: t("Please enter project name") }]}
              >
                <Input placeholder={t("Enter project name (e.g. Website Redesign)")} />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="client"
                label={t("Client")}
                rules={[{ required: true, message: t("Please select a client") }]}
              >
                <CustomerSelect
                  placeholder={t("Select or search client")}
                  customers={clientsArray.map(o => o.customer)}
                  branch={branchSel.clientBranch}
                  onChange={(id, branchId, customer) =>
                    setBranchSel({
                      clientBranch: branchId,
                      billTo:
                        (branchId && findBranch(customer, branchId)?.defaultBillTo) || "branch"
                    })
                  }
                />
              </Form.Item>
            </Col>
            {selectedClient?.branches?.length ? (
              <Col xs={24}>
                <CustomerBranchPicker
                  customer={selectedClient}
                  value={branchSel}
                  onChange={setBranchSel}
                  showBranchSelect={false}
                />
              </Col>
            ) : null}

            <Col xs={24} md={12}>
              <Form.Item
                name="department"
                label={t("Department")}
                rules={[{ required: true, message: t("Please select a department") }]}
              >
                <Select placeholder={t("Select department")} options={departments} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="status" label={t("Status")}>
                <Select placeholder={t("Select status")} options={projectStatusArray} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label={t("Start Date")}
                name="startDate"
                rules={[
                  { required: true, message: t("Please select a start date") },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const endDate = getFieldValue("endDate");
                      if (
                        !value ||
                        !endDate ||
                        value.isBefore(endDate) ||
                        value.isSame(endDate, "day")
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(t("Start date must be before or same as end date"));
                    }
                  })
                ]}
              >
                <DatePicker
                  className="w-100"
                  format="DD/MMM/YYYY"
                  placeholder={t("Select start date")}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label={t("End Date")}
                name="endDate"
                rules={[
                  { required: true, message: t("Please select an end date") },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startDate = getFieldValue("startDate");
                      if (
                        !value ||
                        !startDate ||
                        value.isAfter(startDate) ||
                        value.isSame(startDate, "day")
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(t("End date must be after or same as start date"));
                    }
                  })
                ]}
              >
                <DatePicker
                  className="w-100"
                  format="DD/MMM/YYYY"
                  placeholder={t("Select end date")}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="priority" label={t("Priority")}>
                <Select
                  options={[
                    { value: "LOW", label: t("Low") },
                    { value: "MEDIUM", label: t("Medium") },
                    { value: "HIGH", label: t("High") }
                  ]}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="rateType" label={t("Rate Type")}>
                <Select
                  options={[
                    { value: "HOURLY", label: t("Hourly") },
                    { value: "FIXED", label: t("Fixed") }
                  ]}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="amount"
                label={
                  <Space>
                    {t("Rate")}
                    <Tooltip title={t("Enter the project rate based on selected type")}>
                      <InfoCircleOutlined style={{ color: token.colorTextTertiary }} />
                    </Tooltip>
                  </Space>
                }
              >
                <Input
                  type="number"
                  placeholder={t("Enter amount")}
                  addonAfter={
                    <Form.Item name="currency" noStyle>
                      <Select
                        aria-label={t("Currency")}
                        popupMatchSelectWidth={false}
                        style={{ minWidth: 72 }}
                        options={currenciesArray.map(c => ({ label: c.label, value: c.value }))}
                      />
                    </Form.Item>
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="projectLeader" label={t("Project Leader")}>
                <Select showSearch placeholder={t("Search & select leader")} options={employees} />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item name="financialController" label={t("Financial Controller")}>
                <Select
                  showSearch
                  placeholder={t("Search & select controller")}
                  options={employees}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item name="team" label={t("Project Team")}>
                <Select
                  mode="multiple"
                  placeholder={t("Select team members")}
                  options={employees.map(emp => ({
                    ...emp,
                    disabled: employeeObject?._id === emp.value
                  }))}
                  maxTagCount="responsive"
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item name="description" label={t("Project Description")}>
                <TextArea
                  rows={4}
                  placeholder={t("Briefly describe the project scope and objectives...")}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item name="files" label={t("Project Documents")}>
                <DragUpload />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <div className="inventory-section mt-2">
                <Title level={5} className="mb-3">
                  <Space>
                    {t("Inventory Requirement")}
                    <Text type="secondary" style={{ fontSize: "12px", fontWeight: "normal" }}>
                      ({t("Optional")})
                    </Text>
                  </Space>
                </Title>
                <InventoryUsage form={form} inventoryList={inventoryList} />
              </div>
            </Col>
          </Row>
        </Form>
      )
    }
  ];

  return (
    <div className="AddEditProject">
      {isEditMode ? (
        <MainButtonComp text="Edit" editBtn onClick={showModal} />
      ) : (
        <MainButtonComp text="Add Project" addBtn onClick={showModal} />
      )}

      <GeneralModal
        title={isEditMode ? t("Edit Project") : t("Create New Project")}
        onOk={() => form.submit()}
        open={isModalOpen}
        okText={isEditMode ? t("Update Project") : t("Create Project")}
        confirmLoading={loading}
        onCancel={handleCancel}
        items={items}
        width={800}
      />
    </div>
  );
};

export default AddEditProject;
